import { useRef } from 'react'

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import {
  CounterBadge,
  IconChevronLeft,
  IconChevronRight,
  IconFileMagnifyingGlass,
  IconSpin
} from '@swaggerhub-portal/component-library'
import clsx from 'clsx'
import { Trans } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { SearchResultTile } from './SearchResultTile'
import appDefaults from '~/app.defaults.json'
import { useDarkMode } from '~/components/DarkMode/hook'
import { SEARCH_URL, searchTabs } from '~/components/SearchModal/constants'
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll'
import { useTabScroll } from '~/hooks/useTabScroll'
import emptySearchResultImageDarkDesktop from '~/images/consumer/search/empty-search-results-dark-desktop.svg'
import emptySearchResultImageDarkMobile from '~/images/consumer/search/empty-search-results-dark-mobile.svg'
import emptySearchResultsImage from '~/images/consumer/search/empty-search-results.svg'
import { SEARCH_TAB_TYPE, SearchNoOfElements, SearchResult } from '~/types/search'
import { SearchTab } from '~/types/ui.components'

const NoResults = () => {
  const { isDark } = useDarkMode()
  const isDesktop = useMediaQuery(appDefaults.media.desktop, { initializeWithValue: false, defaultValue: true })
  const darkModeImage = isDesktop ? emptySearchResultImageDarkDesktop : emptySearchResultImageDarkMobile

  return (
    <div className="flex flex-col items-center pt-20 font-medium text-neutral-98 dark:text-neutral-20">
      <span className="pb-1 text-body1">
        <Trans i18nKey="components.ui.searchModal.noResults.title" />
      </span>
      <span className="pb-10 text-body3">
        <Trans i18nKey="components.ui.searchModal.noResults.description" />
      </span>
      <img src={isDark() ? darkModeImage : emptySearchResultsImage} alt="no results" width={224.7} height={140} />
    </div>
  )
}

const EmptySearch = () => (
  <div className="flex flex-col items-center pt-[7.5rem]">
    <IconFileMagnifyingGlass />
    <span className="pt-6 font-medium text-body2 text-neutral-70 dark:text-neutral-40">
      <Trans i18nKey="components.ui.searchModal.empty" />
    </span>
  </div>
)

const TabListContent = ({
  tabs, noOfElements, onTabChange
} : {
  tabs: SearchTab[], noOfElements: SearchNoOfElements, onTabChange: (type: SEARCH_TAB_TYPE) => void
}) => {
  const getCount = (type: SEARCH_TAB_TYPE) => {
    const elements = {
      [SEARCH_TAB_TYPE.DOCS]: noOfElements.documents || 0,
      [SEARCH_TAB_TYPE.APIS]: noOfElements.apis || 0,
      [SEARCH_TAB_TYPE.ENDPOINTS]: noOfElements.endpoints || 0,
      [SEARCH_TAB_TYPE.SCHEMAS]: noOfElements.schemas || 0,
    }
    if (type === SEARCH_TAB_TYPE.ALL) {
      return elements[SEARCH_TAB_TYPE.DOCS] + elements[SEARCH_TAB_TYPE.APIS] + elements[SEARCH_TAB_TYPE.ENDPOINTS] + elements[SEARCH_TAB_TYPE.SCHEMAS]
    }
    return elements[type]
  }

  return (
    <>
      {tabs.map((tab: SearchTab) => (
        <Tab key={tab.type} className="flex-1 text-center border-b row border-neutral-30 dark:border-neutral-80 pb-[9px] md:pb-[5px]" onClick={() => onTabChange(tab.type)}>
          {({ selected }) => (
            <div className={clsx(
              'flex items-center justify-center px-3.5',
              'pt-3 pb-2.5 -mb-2.5 md:pt-2 md:pb-1.5 md:-mb-1.5',
              selected ? 'border-b-2 border-neutral-98 dark:border-neutral-30' : 'border-b-2 border-transparent'
            )}>
              <span className={clsx('mr-2.5 text-body2 font-medium', selected ? 'text-neutral-98 dark:text-neutral-10' : ' text-neutral-70 dark:text-neutral-40')}>
                {tab.title}
              </span>
              <CounterBadge counter={getCount(tab.type)} />
            </div>
          )}
        </Tab>
      ))}
    </>
  )
}

interface SearchResultsProps {
  results: SearchResult[]
  totalElements: number
  inputLength: number
  minQueryLength: number
  onClose: () => void
  isLoading?: boolean
  isMobile?: boolean
  onTabChange: (type: SEARCH_TAB_TYPE) => void
  noOfElements: SearchNoOfElements
  searchParams?: Record<string, string>

}

export const SearchResults = ({
  results,
  totalElements,
  isLoading = false,
  inputLength,
  minQueryLength,
  onClose,
  isMobile = false,
  onTabChange,
  noOfElements,
  searchParams,
}: SearchResultsProps) => {
  const { tabListRef, canScrollLeft, canScrollRight, shouldShowArrows } = useTabScroll(searchTabs)

  const infiniteScrollContainerRef = useRef<HTMLDivElement>(null)

  const { data, isFetching, fetchMore } = useInfiniteScroll<SearchResult & { id?: string }>(
    SEARCH_URL,
    results ?? [],
    totalElements,
    infiniteScrollContainerRef.current,
    { searchParams },
  )

  return (
    <TabGroup>
      {!shouldShowArrows() ?
        <TabList ref={tabListRef} className="flex w-full">
          <TabListContent tabs={searchTabs} noOfElements={noOfElements} onTabChange={onTabChange} />
        </TabList> :
        <div className="relative flex">
          {canScrollLeft && (
            <span className="absolute left-[-1px] flex h-full">
              <span className="flex items-center h-full px-1 bg-white text-neutral-70 dark:text-neutral-40 dark:bg-neutral-98"><IconChevronLeft /></span>
              <span className="w-4 h-full bg-gradient-to-r from-white to-transparent dark:bg-gradient-to-l dark:from-transparent dark:to-neutral-98" />
            </span>
          )}
          <TabList ref={tabListRef} className="flex w-full overflow-x-scroll scrollbar-hide">
            <TabListContent tabs={searchTabs} noOfElements={noOfElements} onTabChange={onTabChange} />
          </TabList>
          {canScrollRight && (
            <span className="absolute right-[-1px] flex h-full">
              <span className="w-4 h-full bg-gradient-to-l from-white to-transparent dark:from-transparent dark:to-neutral-98 dark:bg-gradient-to-r" />
              <span className="flex items-center h-full px-1 bg-white text-neutral-70 dark:text-neutral-40 dark:bg-neutral-98"><IconChevronRight /></span>
            </span>
          )}
        </div>
      }
      <TabPanels>
        {searchTabs.map(tab => (
          <TabPanel
            key={tab.type}
            ref={infiniteScrollContainerRef}
            onScroll={({ target }) => fetchMore(target as HTMLDivElement)}
            className={clsx(
              'overflow-y-auto',
              !isMobile && inputLength >= minQueryLength && data.length && 'border-b border-neutral-30 dark:border-neutral-80',
              !isMobile ? ' h-[27.063rem]' : 'h-[calc(100vh-16.25rem)] md:h-[calc(100vh-15.75rem)]'
            )}
          >
            {inputLength < minQueryLength ?
              <EmptySearch /> :
              (!isLoading && data.length === 0 ?
                <NoResults /> :
                <ul className="pt-1 w-100">
                  {data.map((result, i) => ( // eslint-disable-next-line react/no-array-index-key
                    <li key={`${result.title}-${i}`}>
                      <SearchResultTile result={result} onClose={onClose} tileKey={`${result.title}-${i}`} searchValue={searchParams?.q} />
                    </li>)
                  )}
                </ul>)}
            {isFetching && (
              <div className="flex items-center justify-center pt-2 pb-4">
                <IconSpin width={isMobile ? 36 : 48} height={isMobile ? 36 : 48} className="mx-auto text-consumer-blue dark:text-dark-consumer-blue" />
              </div>
            )}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  )
}